import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import "./PFHBannerModal.css";

interface PFHBannerModalProps {
  remoteConfig: {
    enabled: boolean;
    image_url: string;
    mobile_image_url: string;
    cta_url: string;
    valid_from: string;
    valid_until: string;
    video_url?: string;
    cta_text?: string;
  } | null;
  onClose: () => void;
  onImageClick: (url: string) => void;
}

const PFHBannerModal: React.FC<PFHBannerModalProps> = ({
  remoteConfig,
  onClose,
  onImageClick,
}) => {
  // Images and URLs for vertical stack
  const bannerImages = [
    "https://hfjekfswossjlpcndyye.supabase.co/storage/v1/object/public/images/Order1-2.webp",
    "https://hfjekfswossjlpcndyye.supabase.co/storage/v1/object/public/images/Order3-4.webp",
  ];

  const bannerUrls = [
    "https://nationalfinanceolympiad.com/1-2-guidebook",
    "https://nationalfinanceolympiad.com/3-4-guidebook-2",
  ];

  const [isImageLoading, setIsImageLoading] = useState(true);
  const [showModal, setShowModal] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  // Commented out video-related code
  // const hasVideo = (remoteConfig as any).video_url;
  // const [isVideoLoading, setIsVideoLoading] = useState(!!hasVideo);
  // const videoRef = React.useRef<HTMLVideoElement>(null);
  // const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  // const [isVideoMuted, setIsVideoMuted] = useState(true);
  // const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    const hasSeenModal = sessionStorage.getItem("hasSeenPFHBanner");
    if (hasSeenModal === "true") {
      setShowModal(false);
    }
  }, []);

  const handleImageClick = (index: number) => {
    const clickedUrl = bannerUrls[index];
    trackData("user_clicked_banner", null, null, {
      banner_type: "pfh_banner_modal",
      image_index: index,
      click_url: clickedUrl,
    });
    onImageClick(clickedUrl);
  };

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => {
      const newSet = new Set(prev);
      newSet.add(index);
      if (newSet.size === bannerImages.length) {
        setIsImageLoading(false);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    setShowModal(false);
    sessionStorage.setItem("hasSeenPFHBanner", "true");
    onClose();
  };

  const isBannerValid = () => {
    if (!remoteConfig) return false;

    const now = new Date();
    const validFrom = new Date(remoteConfig.valid_from);
    const validUntil = new Date(remoteConfig.valid_until);

    return now >= validFrom && now <= validUntil;
  };

  if (!showModal || !remoteConfig?.enabled || !isBannerValid()) {
    return null;
  }

  // Commented out - no longer using remoteConfig image URLs
  // const imageUrl = isMobile
  //   ? remoteConfig.mobile_image_url
  //   : remoteConfig.image_url;

  // const ctaText = (remoteConfig as any).cta_text || "Explore";

  return (
    <div onClick={handleClose} className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {isImageLoading && (
          <div className="modal-loading">
            <CircularProgress />
          </div>
        )}
        <div className="modal-images-stack">
          {bannerImages.map((image, index) => (
            <div key={index} className="modal-image-wrapper">
              <img
                src={image}
                alt={`Banner ${index + 1}`}
                className="modal-banner-image"
                onClick={() => handleImageClick(index)}
                onLoad={() => handleImageLoad(index)}
              />
            </div>
          ))}
        </div>

        <button
          className="modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Commented out video code */}
        {/* {(hasVideo ? isVideoLoading : isImageLoading) && (
          <div className="modal-loading">
            <CircularProgress />
          </div>
        )}
        {hasVideo ? (
          <div style={{ position: "relative", width: "100%" }}>
            {!hasUserInteracted && (
              <button
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  fontSize: 20,
                  fontWeight: 600,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setHasUserInteracted(true);
                  setIsVideoMuted(false);
                  videoRef.current?.play();
                }}
              >
                Click here to proceed
              </button>
            )}
            <button
              onClick={onImageClick}
              style={{
                width: "100%",
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                position: "relative",
                display: isVideoLoading ? "none" : "block",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <video
                ref={videoRef}
                src={(remoteConfig as any).video_url}
                className="pfh-banner-image"
                style={{ width: "100%", height: "auto", display: "block" }}
                autoPlay={hasUserInteracted}
                loop
                muted={isVideoMuted}
                playsInline
                onLoadedData={() => setIsVideoLoading(false)}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
              />
              {!isVideoPlaying && (
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0,0,0,0.5)",
                    borderRadius: "50%",
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    videoRef.current?.play();
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="white">
                    <polygon points="10,8 26,16 10,24" />
                  </svg>
                </span>
              )}
              <span
                style={{
                  position: "absolute",
                  top: 12,
                  right: hasUserInteracted ? 60 : 12,
                  zIndex: 3,
                  background: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVideoMuted((m) => !m);
                }}
              >
                {isVideoMuted ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M4 9v6h4l5 5V4L8 9H4zm13.5 3c0-1.77-.77-3.29-2-4.29v8.58c1.23-1 2-2.52 2-4.29z" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M16.5 12c0-1.77-.77-3.29-2-4.29v8.58c1.23-1 2-2.52 2-4.29zm3.5 0c0 2.53-1.17 4.77-3 6.18v-2.06c1.19-1.02 2-2.54 2-4.12s-.81-3.1-2-4.12V5.82c1.83 1.41 3 3.65 3 6.18zM4 9v6h4l5 5V4L8 9H4z" />
                  </svg>
                )}
              </span>
              <span
                style={{
                  left: 0,
                  right: 0,
                  bottom: 24,
                  background: "rgba(36, 108, 193, 1)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 18,
                  padding: "12px 12px",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                {ctaText}
              </span>
            </button>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt="Success Banner"
            onClick={onImageClick}
            onLoad={() => setIsImageLoading(false)}
            style={{
              cursor: "pointer",
              width: "100%",
              height: "auto",
              display: isImageLoading ? "none" : "block",
            }}
          />
        )}
        {!hasVideo ||
          (hasUserInteracted && (
            <button
              className="modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ))} */}
      </div>
    </div>
  );
};

export default PFHBannerModal;
